import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React from 'react'
import { PUSH_SERVICES, testRecipient } from '../helpers/testSend'
import { STATUS, TEXT } from '../theme/tokens'

/**
 * The inputs of a test send, shared by the Test template page and the
 * pipeline wizard's test panel, so both ask for the same recipient per
 * channel and report the outcome the same way. The rules behind them are
 * `testRecipient` and `variablesToAsk` in helpers/testSend.
 */

/** The empty recipient form (`values` of TestRecipientFields). */
export const EMPTY_RECIPIENT = {
    name: '',
    email: '',
    ccText: '',
    mobile: '',
    // Slack: an email, member id or channel id. Push: a device token and its
    // platform. Both used to get a Mobile field the sender cannot use.
    slackTarget: '',
    pushToken: '',
    pushService: 'GCM',
    emailTouched: false,
}

/**
 * The recipient of a test send for one channel: a name, then the email and
 * CC (email), the Slack target (Slack), the device token and platform
 * (Push), or the mobile number (WhatsApp, SMS, voice). Rendered as Grid
 * items: put it inside a Grid container. `onChange` receives a patch of
 * `values`.
 */
export const TestRecipientFields = ({
    commType,
    values,
    onChange,
    itemProps = { xs: 12 },
}) => {
    const check = testRecipient(commType, values)
    const field = (key) => (event) => onChange({ [key]: event.target.value })
    return (
        <>
            <Grid item {...itemProps}>
                <TextField
                    variant="outlined"
                    label="Enter receiver Name"
                    value={values.name}
                    placeholder="Eg: John Doe"
                    onChange={field('name')}
                    fullWidth
                />
            </Grid>
            {commType === 'email' ? (
                <>
                    <Grid item {...itemProps}>
                        <TextField
                            variant="outlined"
                            label="Enter receiver email"
                            value={values.email}
                            type="email"
                            placeholder="someone@example.com"
                            onChange={field('email')}
                            onBlur={() => onChange({ emailTouched: true })}
                            error={values.emailTouched && check.emailInvalid}
                            helperText={
                                values.emailTouched && check.emailInvalid
                                    ? 'Enter a valid email address, like name@example.com'
                                    : ' '
                            }
                            fullWidth
                        />
                    </Grid>
                    <Grid item {...itemProps}>
                        <TextField
                            variant="outlined"
                            label="CC"
                            value={values.ccText}
                            placeholder="cc: test@example.com, john@example.com"
                            onChange={field('ccText')}
                            error={check.invalidCcs.length > 0}
                            helperText={
                                check.invalidCcs.length > 0
                                    ? `Not an email address: ${check.invalidCcs.join(
                                          ', '
                                      )}`
                                    : 'Separate addresses with commas'
                            }
                            fullWidth
                        />
                    </Grid>
                </>
            ) : commType === 'slack' ? (
                <Grid item {...itemProps}>
                    <TextField
                        variant="outlined"
                        label="Slack email, member ID or channel ID"
                        value={values.slackTarget}
                        placeholder="name@bhanzu.com, U012ABCDEF or C012ABCDEF"
                        onChange={field('slackTarget')}
                        error={check.slackInvalid}
                        helperText={
                            check.slackInvalid
                                ? 'Enter an email address, a member ID (U...) or a channel ID (C...)'
                                : 'Where the test message goes in Slack'
                        }
                        fullWidth
                    />
                </Grid>
            ) : commType === 'push_notification' ? (
                <>
                    <Grid item {...itemProps}>
                        <TextField
                            variant="outlined"
                            label="Device token"
                            value={values.pushToken}
                            placeholder="The device's FCM or APNs token"
                            onChange={field('pushToken')}
                            helperText="The test notification goes to this device"
                            fullWidth
                        />
                    </Grid>
                    <Grid item {...itemProps}>
                        <TextField
                            select
                            variant="outlined"
                            label="Device platform"
                            value={values.pushService}
                            onChange={field('pushService')}
                            fullWidth
                        >
                            {PUSH_SERVICES.map((s) => (
                                <MenuItem key={s.value} value={s.value}>
                                    {s.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </>
            ) : (
                <Grid item {...itemProps}>
                    <TextField
                        variant="outlined"
                        label="Mobile"
                        value={values.mobile}
                        placeholder="+91XXXXXXXXXX"
                        onChange={field('mobile')}
                        fullWidth
                    />
                </Grid>
            )}
        </>
    )
}

TestRecipientFields.propTypes = {
    commType: PropTypes.string.isRequired,
    values: PropTypes.shape({
        name: PropTypes.string,
        email: PropTypes.string,
        ccText: PropTypes.string,
        mobile: PropTypes.string,
        slackTarget: PropTypes.string,
        pushToken: PropTypes.string,
        pushService: PropTypes.string,
        emailTouched: PropTypes.bool,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    // Grid sizes of each field.
    itemProps: PropTypes.object,
}

/**
 * One required field per template variable the sender cannot fill itself
 * (`variablesToAsk`), with a heading. Nothing when there are none.
 */
export const TestVariableFields = ({ variables, values, onChange }) => {
    if (!variables.length) return null
    return (
        <Box>
            <Typography
                variant="overline"
                component="h2"
                sx={{ color: TEXT.muted }}
            >
                Template variables
            </Typography>
            <Typography
                variant="caption"
                component="p"
                sx={{ color: TEXT.muted, mb: 1.5 }}
            >
                Test values for this send only. In a real send, the event that
                triggers the template provides them.
            </Typography>
            <Grid container spacing={1.5}>
                {variables.map((v) => (
                    <Grid item xs={12} sm={6} key={v}>
                        <TextField
                            variant="outlined"
                            label={v}
                            value={values[v] ?? ''}
                            onChange={(event) =>
                                onChange({ ...values, [v]: event.target.value })
                            }
                            helperText={`Fills \${${v}}`}
                            required
                            fullWidth
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    )
}

TestVariableFields.propTypes = {
    variables: PropTypes.arrayOf(PropTypes.string).isRequired,
    values: PropTypes.object.isRequired,
    // Receives the whole new values object.
    onChange: PropTypes.func.isRequired,
}

const STATUS_COLOR = {
    sending: TEXT.muted,
    pending: TEXT.muted,
    delivered: STATUS.success.fg,
    failed: STATUS.error.fg,
    error: STATUS.error.fg,
    timeout: STATUS.warning.fg,
}

/** The test send's progress and outcome (from useTestSend), as a status line. */
export const TestSendStatus = ({ sendState, statusMessage, busy }) => {
    if (!statusMessage) return null
    return (
        <Box
            role="status"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
            {busy && <CircularProgress size={14} sx={{ color: TEXT.muted }} />}
            <Typography
                variant="caption"
                sx={{ color: STATUS_COLOR[sendState] }}
            >
                {statusMessage}
            </Typography>
        </Box>
    )
}

TestSendStatus.propTypes = {
    sendState: PropTypes.string.isRequired,
    statusMessage: PropTypes.string,
    busy: PropTypes.bool,
}
